import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardIcon, ScaleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const FEATURE_TITLE = "Treatment Recommendations";
const FEATURE_DESCRIPTION = "Evidence-based treatment options with dosage, route, frequency, and duration, plus alternative treatments and adverse reaction scoring.";

const TreatmentRecommendationsPage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ClipboardIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Evidence-Based Options</h3>
              <p className="text-text">
                Our treatment recommendations are based on the latest clinical evidence 
                and guidelines, updated regularly to ensure the most current protocols.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Recommendations from latest clinical guidelines</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Literature-based evidence rankings</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Monthly updates to treatment protocols</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ScaleIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Comprehensive Dosing</h3>
              <p className="text-text">
                Receive detailed treatment options with specific dosage, route, frequency, 
                and duration recommendations personalized for the patient's condition.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Patient-specific dosage calculations</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Multiple administration route options</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Duration optimization based on infection type</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ExclamationCircleIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Risk Assessment</h3>
              <p className="text-text">
                We provide alternative treatment options and score potential adverse reactions, 
                helping clinicians balance efficacy with safety concerns.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Alternative treatment pathways</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Adverse reaction probability scoring</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Contraindication alerts and warnings</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="mt-16 bg-treatment-light p-8 rounded-lg shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-treatment-header mb-4">Sample Treatment Recommendation</h2>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="bg-treatment-header px-4 py-3">
              <h3 className="text-lg font-semibold text-text-light">E. coli Urinary Tract Infection - Uncomplicated</h3>
            </div>
            <div className="p-4 bg-white">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-treatment-header mb-3">Primary Treatment Option</h4>
                  <div className="bg-treatment-light p-4 rounded border border-border">
                    <p className="font-semibold mb-1">Nitrofurantoin</p>
                    <ul className="text-sm space-y-1">
                      <li><span className="font-medium">Dosage:</span> 100 mg</li>
                      <li><span className="font-medium">Route:</span> Oral</li>
                      <li><span className="font-medium">Frequency:</span> Twice daily</li>
                      <li><span className="font-medium">Duration:</span> 5 days</li>
                      <li><span className="font-medium">Resistance Score:</span> 15 (Low)</li>
                      <li><span className="font-medium">Adverse Reaction Risk:</span> 20 (Low)</li>
                    </ul>
                    <div className="mt-3 p-2 bg-section-light border border-border rounded text-sm">
                      <p className="font-medium text-treatment-header">Note:</p>
                      <p>Take with food to minimize GI upset. Ensure adequate hydration.</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-treatment-alt mb-3">Alternative Options</h4>
                  <div className="space-y-4">
                    <div className="bg-treatment-light p-3 rounded border border-border">
                      <p className="font-semibold mb-1">Trimethoprim-Sulfamethoxazole</p>
                      <ul className="text-sm space-y-1">
                        <li><span className="font-medium">Dosage:</span> 160/800 mg</li>
                        <li><span className="font-medium">Frequency:</span> Twice daily for 3 days</li>
                        <li><span className="font-medium">Resistance Score:</span> 30 (Moderate)</li>
                      </ul>
                    </div>
                    <div className="bg-treatment-light p-3 rounded border border-border">
                      <p className="font-semibold mb-1">Ciprofloxacin</p>
                      <ul className="text-sm space-y-1">
                        <li><span className="font-medium">Dosage:</span> 250 mg</li>
                        <li><span className="font-medium">Frequency:</span> Twice daily for 3 days</li>
                        <li><span className="font-medium">Resistance Score:</span> 45 (Moderate-High)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentRecommendationsPage; 